<template>
    <modal
        v-if="!lockedOut"
        :show.sync="showModal"
        body-classes="p-0"
        :modal-classes="`modal-xl ${$style.modalClasses}`"
    >
        <card class="card-form mb-0">
            <template slot="header">
                <h2 class="modal-title">{{ title }}</h2>
                <h4 v-if="!isWillCall" :class="$style.advanceNotice">
                    * Delivery requests must be placed at least 48 hours before
                    your desired delivery date.
                </h4>
            </template>
            <div :class="$style.header">
                <div :class="$style.customer">
                    <div :class="$style.label">Customer / Showroom</div>
                    <div :class="$style.title">
                        {{ receiptLogs[0].customer.name }}
                    </div>
                </div>
                <div :class="$style.designer">
                    <div :class="$style.label">Designer</div>
                    <div :class="$style.title">
                        {{ designerName }}
                    </div>
                </div>
            </div>
            <el-table
                v-if="tableItems.length"
                max-height="350"
                :data="tableItems"
            >
                <el-table-column
                    v-for="column in tableColumns"
                    :key="column.label"
                    :width="column.width"
                    :min-width="column.minWidth"
                    :prop="column.prop"
                    :label="column.label"
                    :formatter="column.formatter"
                    :show-overflow-tooltip="true"
                />
            </el-table>
            <ValidationObserver v-slot="{ invalid, validate, pristine }">
                <form
                    class="mt-3"
                    autocomplete="off"
                    @submit.prevent="validate().then(requestDelivery)"
                >
                    <div class="row">
                        <div v-if="!isWillCall" class="col-3">
                            <base-input
                                v-model="delivery.locationName"
                                name="location"
                                type="text"
                                label="Location Name"
                            />
                        </div>
                        <div class="col-3">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery Date"
                                rules="required"
                            >
                                <base-input
                                    name="deliveryDate"
                                    type="date"
                                    :label="deliveryDateLabel"
                                    :error="errors[0]"
                                >
                                    <el-date-picker
                                        v-model="delivery.deliveryDate"
                                        :picker-options="{
                                            disabledDate: isDeliveryDateInvalid,
                                        }"
                                        format="MM/dd/yyyy"
                                        type="date"
                                        placeholder="Select date"
                                    />
                                </base-input>
                            </ValidationProvider>
                        </div>
                        <div :class="isWillCall ? 'col-9' : 'col-6'">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery Window"
                                rules="required"
                            >
                                <label>{{ deliveryWindowLabel }}</label>
                                <div class="form-group">
                                    <el-radio-group
                                        v-model="deliveryWindow"
                                        size="medium"
                                        style="display: block"
                                    >
                                        <el-radio-button
                                            v-for="option in deliveryWindowOptions"
                                            :key="option.value"
                                            :label="option.label"
                                        />
                                    </el-radio-group>
                                    <label class="error">{{ errors[0] }}</label>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Job Site Contact Name"
                                rules="required"
                            >
                                <base-input
                                    v-model="delivery.contacts[0].name"
                                    name="jobSiteContactName"
                                    type="text"
                                    :label="contactNameLabel"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Job Site Contact Phone"
                                rules="required"
                            >
                                <div class="form-group has-label">
                                    <label>{{ contactPhoneLabel }}</label>
                                    <masked-input
                                        v-model="delivery.contacts[0].phone"
                                        class="form-control"
                                        name="jobSiteContactPhone"
                                        autocomplete="off"
                                        type="tel"
                                        :mask="phoneMask"
                                        :guide="true"
                                        placeholder="(123) 456-7890"
                                        :placeholder-char="'\u2000'"
                                        :error="errors[0]"
                                    />
                                </div>
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Job Site Contact Email"
                                rules="required"
                            >
                                <base-input
                                    v-model="delivery.contacts[0].email"
                                    name="jobSiteContactEmail"
                                    type="text"
                                    :label="contactEmailLabel"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                    </div>
                    <div v-if="!isWillCall" class="row">
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery Address"
                                rules="required"
                            >
                                <base-input
                                    v-model="delivery.deliveryAddress"
                                    name="address"
                                    type="text"
                                    label="Delivery Address"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery City"
                                rules="required"
                            >
                                <base-input
                                    v-model="delivery.deliveryCity"
                                    name="city"
                                    type="text"
                                    label="Delivery City"
                                    :error="errors[0]"
                                />
                            </ValidationProvider>
                        </div>
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Delivery Zip"
                                rules="required"
                            >
                                <div class="form-group has-label">
                                    <label>Delivery Zip</label>
                                    <masked-input
                                        v-model="delivery.deliveryZip"
                                        class="form-control"
                                        name="zip"
                                        autocomplete="off"
                                        type="text"
                                        :mask="zipMask"
                                        :guide="true"
                                        placeholder-char="#"
                                        :error="errors[0]"
                                    />
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div v-if="!isWillCall" class="row">
                        <div class="col-4">
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Deliver Into"
                                rules="required"
                            >
                                <label>Deliver Into</label>
                                <div class="form-group">
                                    <el-radio-group
                                        v-model="delivery.deliverInto"
                                    >
                                        <el-radio-button label="House" />
                                        <el-radio-button label="Garage" />
                                        <el-radio-button label="Other" />
                                    </el-radio-group>
                                    <label class="error">{{ errors[0] }}</label>
                                </div>
                            </ValidationProvider>
                        </div>
                        <div class="col-2">
                            <label>Elevator?</label>
                            <div class="form-group">
                                <el-switch
                                    v-model="delivery.hasElevator"
                                    active-text="Yes"
                                    inactive-text="No"
                                />
                            </div>
                        </div>
                        <div class="col-3">
                            <label># of Interior Stairs</label>
                            <el-input-number
                                v-model="delivery.interiorStairsCount"
                                :min="0"
                            />
                        </div>
                        <div class="col-3">
                            <label># of Exterior Stairs</label>
                            <el-input-number
                                v-model="delivery.exteriorStairsCount"
                                :min="0"
                            />
                        </div>
                    </div>
                    <div
                        v-for="(contact, index) in additionalContacts"
                        :key="index"
                        class="row"
                    >
                        <div class="col-4">
                            <base-input
                                v-model="delivery.contacts[index + 1].name"
                                v-focus
                                focus
                                :name="`jobSiteContactName${index + 1}`"
                                type="text"
                                :label="`${contactNameLabel} ${index + 1}`"
                            />
                        </div>
                        <div class="col-4">
                            <div class="form-group has-label">
                                <label
                                    >{{ contactPhoneLabel }}
                                    {{ index + 1 }}</label
                                >
                                <masked-input
                                    v-model="delivery.contacts[index + 1].phone"
                                    class="form-control"
                                    :name="`jobSiteContactPhone${index + 1}`"
                                    autocomplete="off"
                                    type="tel"
                                    :mask="phoneMask"
                                    :guide="true"
                                    placeholder="(123) 456-7890"
                                    :placeholder-char="'\u2000'"
                                />
                            </div>
                        </div>
                        <div class="col-4">
                            <div class="form-group has-label">
                                <base-input
                                    v-model="delivery.contacts[index + 1].email"
                                    :name="`jobSiteContactEmail${index + 1}`"
                                    type="text"
                                    :label="`${contactEmailLabel} ${index + 1}`"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="mt-3">
                        <base-button
                            class="pl-0"
                            link
                            type="primary"
                            @click="delivery.contacts.push({})"
                        >
                            Add Additional Contact
                        </base-button>
                    </div>
                    <div>
                        <base-input
                            v-if="showNotesTextArea"
                            name="deliveryNotes"
                            :label="deliveryNotesLabel"
                        >
                            <textarea
                                v-model="delivery.deliveryNotes"
                                v-focus
                                style="max-height: unset"
                                class="form-control"
                                rows="3"
                            />
                        </base-input>
                        <base-button
                            v-if="!showNotesTextArea"
                            class="pl-0"
                            link
                            type="primary"
                            @click="showNotesTextArea = true"
                        >
                            {{ deliveryNotesLabel }}
                        </base-button>
                    </div>
                    <div v-if="!isWillCall">
                        <p class="small">
                            * Please be sure to include in the Special
                            Instructions if your delivery requires a special
                            sized truck; if the site is difficult to access, or
                            has steep or narrow roads and/or driveways; if there
                            is a long carry; if we need a gate or door code,
                            etc. Please note that these conditions may cause
                            delays in the delivery and may increase the time we
                            spend on site which could result in additional
                            charges.
                        </p>
                        <p class="small">
                            * We no longer do 3rd party billing. We will bill
                            you for the delivery -- if you would like your
                            customer to pay for the delivery, we are happy to
                            accept a check from your customer or you may call us
                            with their credit card information. All new accounts
                            will be required to have a credit card on file for a
                            minimum of 5 deliveries at which time we will
                            evaluate the possibility of an open account.
                        </p>
                    </div>
                    <el-divider />
                    <div class="d-flex">
                        <el-checkbox
                            v-model="delivery.agreedToTerms"
                            :class="$style.checkbox"
                        />
                        <span class="ml-2 d-flex">
                            I have read and agree to
                            <span v-if="!isWillCall" class="ml-1 mr-1"
                                >the above and to
                            </span>
                            the
                            <base-button
                                v-if="materialType === 'Cabinets'"
                                link
                                :class="$style.link"
                                type="primary"
                                @click="showCabinetTermsAndConditions = true"
                            >
                                terms and conditions.
                            </base-button>
                            <base-button
                                v-else-if="materialType === 'Furniture'"
                                link
                                :class="$style.link"
                                type="primary"
                                @click="showFurnitureTermsAndConditions = true"
                            >
                                terms and conditions.
                            </base-button>
                            <base-button
                                v-else
                                link
                                :class="$style.link"
                                type="primary"
                                @click="
                                    showCabinetTermsAndConditions = true;
                                    showFurnitureTermsAndConditions = true;
                                "
                            >
                                terms and conditions.
                            </base-button>
                        </span>
                        <label class="error">{{ errors[0] }}</label>
                    </div>
                    <div class="d-flex mt-2">
                        <a href="/docs/baserates.pdf"
                            >Download & view current city/zip code rates.</a
                        >
                    </div>
                    <div :class="$style.actions">
                        <base-button
                            type="default"
                            class="btn-fill"
                            @click="reset()"
                        >
                            Cancel
                        </base-button>
                        <div :class="$style.right">
                            <base-button
                                type="info"
                                class="btn-fill"
                                @click="showModal = false"
                            >
                                Add More Items
                            </base-button>
                            <base-button
                                id="requestDelivery"
                                :disabled="
                                    invalid ||
                                    !delivery.agreedToTerms ||
                                    pristine
                                "
                                native-type="submit"
                                type="primary"
                                class="btn-fill"
                            >
                                {{ requestDeliveryButtonLabel }}
                            </base-button>
                        </div>
                    </div>
                </form>
            </ValidationObserver>
        </card>
        <cabinet-terms-and-conditions-modal
            :show.sync="showCabinetTermsAndConditions"
        />
        <furniture-terms-and-conditions-modal
            :show.sync="showFurnitureTermsAndConditions"
        />
    </modal>
</template>

<script>
import moment from "moment-timezone";
import MaskedInput from "vue-text-mask";
import { phoneMask, zipMask } from "src/helpers/inputMasks";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Modal } from "src/components/UI";
import {
    DatePicker,
    Switch,
    InputNumber,
    RadioGroup,
    RadioButton,
    Checkbox,
    Divider,
} from "element-ui";
import CabinetTermsAndConditionsModal from "./CabinetTermsAndConditionsModal";
import FurnitureTermsAndConditionsModal from "./FurnitureTermsAndConditionsModal";

export default {
    name: "NewDeliveryRequest",
    components: {
        Modal,
        MaskedInput,
        [DatePicker.name]: DatePicker,
        [Switch.name]: Switch,
        [InputNumber.name]: InputNumber,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Checkbox.name]: Checkbox,
        [Divider.name]: Divider,
        ValidationObserver,
        ValidationProvider,
        CabinetTermsAndConditionsModal,
        FurnitureTermsAndConditionsModal,
    },
    props: {
        receiptLogs: {
            type: Array,
            required: true,
        },
        hasPastDueInvoices: {
            type: Boolean,
            required: true,
        },
        lockedOut: {
            type: Boolean,
            required: true,
        },
        isWillCall: {
            type: Boolean,
            required: true,
        },
        show: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            delivery: {},
            emptyDelivery: {
                agreedToTerms: false,
                contacts: [{}],
                hasElevator: false,
                interiorStairsCount: 0,
                exteriorStairsCount: 0,
            },
            showCabinetTermsAndConditions: false,
            showFurnitureTermsAndConditions: false,
            pastDueInvoiceMessageShown: false,
            phoneMask,
            zipMask,
            selectedDeliveryWindow: null,
            selectedDeliverInto: null,
            selectedElevator: null,
            deliveryWindowOptions: [
                { value: "Morning", label: "9AM - 11AM" },
                { value: "Midday", label: "11AM - 1PM" },
                { value: "Afternoon", label: "1PM - 3PM" },
                { value: "Any", label: "Any" },
            ],
            showNotesTextArea: false,
            showModal: false,
            tableColumns: [
                {
                    prop: "receiptLogId",
                    label: "Receipt Log ID",
                },
                {
                    prop: "unitId",
                    label: "Unit ID",
                },
                {
                    prop: "manufacturer.name",
                    label: "Manufacturer",
                },
                {
                    prop: "itemNumber",
                    label: "Item No.",
                },
                {
                    prop: "purchaseOrder",
                    label: "P.O. No.",
                    formatter: (row) =>
                        this.receiptLogs.find(
                            (log) => log.id === row.receiptLogId,
                        ).purchaseOrder,
                },
                {
                    prop: "description",
                    label: "Description",
                },
                {
                    prop: "quantity",
                    label: "Cubic Ft. / Items",
                },
            ],
        };
    },
    computed: {
        title() {
            return this.isWillCall ? "Will Call" : "Request Delivery";
        },
        deliveryDateLabel() {
            return this.isWillCall
                ? "Desired Pickup Date"
                : "Desired Delivery Date";
        },
        deliveryWindowLabel() {
            return this.isWillCall
                ? "Desired Pickup Window"
                : "Desired Delivery Window";
        },
        contactNameLabel() {
            return this.isWillCall ? "Contact Name" : "Job Site Contact Name";
        },
        contactPhoneLabel() {
            return this.isWillCall ? "Contact Phone" : "Job Site Contact Phone";
        },
        contactEmailLabel() {
            return this.isWillCall ? "Contact Email" : "Job Site Contact Email";
        },
        deliveryNotesLabel() {
            return this.isWillCall
                ? "Pick up Notes/Special Instructions"
                : "Delivery Notes/Special Instructions";
        },
        requestDeliveryButtonLabel() {
            return this.isWillCall ? "Request Will Call" : "Request Delivery";
        },
        additionalContacts() {
            // exclude first result
            return this.delivery.contacts.slice(1);
        },
        designerName() {
            if (this.receiptLogs.length > 1) {
                const designers = new Set(
                    this.receiptLogs.map((log) => log.designer?.fullName),
                );
                if (designers.size > 1) return "Multiple";
                return designers.values().next().value;
            }
            return this.receiptLogs[0].designer?.fullName || "N/A";
        },
        deliveryWindow: {
            get() {
                return this.selectedDeliveryWindow;
            },
            set(window) {
                this.selectedDeliveryWindow = window;
                // get the value from the given label
                const { value } = this.deliveryWindowOptions.find(
                    (option) => option.label === window,
                );
                this.delivery.deliveryWindow = value;
            },
        },
        tableItems() {
            return this.receiptLogs.map((log) => log.units).flat();
        },
        materialType() {
            const allTypes = this.receiptLogs.map(
                (log) => log.materialType.description,
            );
            const types = [...new Set(allTypes)];
            if (types.includes("Cabinets") && types.includes("Furniture")) {
                return "Both";
            }
            if (types.includes("Cabinets")) return "Cabinets";
            if (types.includes("Furniture")) return "Furniture";

            return "Unknown";
        },
    },
    watch: {
        show(val) {
            this.showModal = val;
            if (
                val &&
                this.hasPastDueInvoices &&
                !this.pastDueInvoiceMessageShown
            ) {
                this.$emit("showPastDueInvoicesMessage");
                this.pastDueInvoiceMessageShown = true;
            }
        },
        showModal(val) {
            this.$emit("update:show", val);
        },
    },
    created() {
        this.delivery = { ...this.emptyDelivery };
        this.showModal = this.show;
        if (this.hasPastDueInvoices) {
            this.$emit("showPastDueInvoicesMessage");
            this.pastDueInvoiceMessageShown = true;
            // Don't allow a client that is locked out to continue
            // requesting a delivery, they must pay first
            if (this.lockedOut) {
                this.reset();
            }
        }
    },
    methods: {
        isDeliveryDateInvalid(date) {
            const fortyEightHoursFromNow = moment().add(48, "hours");
            const threePMHour = 15;
            const compareDate = moment(date).hour(threePMHour);
            return fortyEightHoursFromNow > compareDate;
        },
        requestDelivery() {
            const deliveryRequestDetails = [];
            this.receiptLogs.forEach((log, index) => {
                const detail = {
                    detailId: index + 1,
                    receiptLogId: log.id,
                    unitIds: log.units.map((unit) => unit.unitId),
                    estimatedCubes: log.units
                        .map((unit) => unit.quantity)
                        .reduce((a, b) => a + b),
                    unitType: log.sizeType,
                    materials: log.materialType.description,
                    designerID: log.designer?.id,
                };
                deliveryRequestDetails.push(detail);
            });
            const deliveryRequest = {
                ...this.delivery,
                deliveryDate: this.delivery.deliveryDate.toDateString(),
                estimatedCubes: deliveryRequestDetails
                    .map((d) => d.estimatedCubes)
                    .reduce((a, b) => a + b),
                deliveryRequestDetails,
                willCall: this.isWillCall,
                schedCustomer: this.receiptLogs[0].customer.id,
                exteriorStairsCount:
                    parseInt(this.delivery.exteriorStairsCount, 10) || 0,
                interiorStairsCount:
                    parseInt(this.delivery.interiorStairsCount, 10) || 0,
            };
            if (this.isWillCall) {
                deliveryRequest.deliveryAddress = "";
                deliveryRequest.deliveryCity = "";
                deliveryRequest.deliveryZip = "";
                deliveryRequest.hasElevator = false;
                deliveryRequest.deliverInto = "";
                deliveryRequest.deliveryAddressVerified = false;
            }
            this.$emit("addDeliveryRequest", deliveryRequest);
        },
        reset() {
            this.delivery = { ...this.emptyDelivery };
            this.selectedDeliveryWindow = null;
            this.showModal = false;
            this.pastDueInvoiceMessageShown = false;
            this.$emit("cancel");
        },
    },
};
</script>

<style module lang="scss">
@import "assets/sass/dashboard/custom/_variables.scss";

.modalClasses {
    min-width: 750px;
}

.header {
    display: flex;
    padding-left: 10px;
    padding-right: 30px;

    .title {
        font-size: 18px;
        color: $black;
        font-weight: 600;
    }

    .customer {
        flex-basis: 70%;
    }
}

.actions {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
}

.actions .right > * {
    margin-right: 10px;
    margin-left: 10px;
}

.checkbox {
    align-self: center;
    margin: 0 !important;
}

.link {
    font-weight: 400;
    padding: 0px;
    margin: 0px;
    margin-left: 3px;
    line-height: 1.5;
}

.advanceNotice {
    margin-top: 20px;
    font-weight: bold;
    color: hsla(349, 72%, 48%, 1) !important;
}
</style>
